import { useState } from 'react';

import { CloudSync } from '@mui/icons-material';
import { Box, Typography, Button, CircularProgress, Alert, TextField } from '@mui/material';

import {
  client,
  getSyncStatusApiV1SheetsSyncSyncIdGet,
  triggerSheetsSyncApiV1SheetsSyncDataTypePost,
} from '../../client';
import { useAuth } from '../../services/auth/AuthContext';
import NotificationService from '../../services/notification/NotificationService';
import { extractSpreadsheetId } from '../../utils/googleSheetsUtils';
import PageLayout from '../PageLayout/PageLayout';
import { PageId } from '../Sidebar/Sidebar';

export enum SyncType {
  BuyerVendor = 'buyer_vendor',
  BuyerByProduct = 'buyer_by_product',
  DateMapping = 'date_mapping',
  CustomerGroup = 'customer_group',
  ProductGroup = 'product_group',
  SalesRepDepartment = 'sales_rep_department',
  CreditReport = 'credit_report',
  Salesmen = 'salesmen',
}

interface GoogleSheetsSyncPageProps {
  title: string;
  syncType: SyncType;
  pageId: PageId;
  defaultSheetUrl?: string;
  defaultRange?: string;
}

export const GoogleSheetsSyncPage = ({ title, syncType, pageId, defaultSheetUrl, defaultRange }: GoogleSheetsSyncPageProps) => {
  const { getToken } = useAuth();
  const [sheetUrl, setSheetUrl] = useState(
    defaultSheetUrl && extractSpreadsheetId(defaultSheetUrl) ? defaultSheetUrl : ''
  );
  const [range, setRange] = useState(defaultRange || '');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [errors, setErrors] = useState<any[]>([]);

  const handleSync = async () => {
    if (!sheetUrl || !range) {
      NotificationService.error('Please provide both a Google Sheets URL and range');
      return;
    }

    const spreadsheetId = extractSpreadsheetId(sheetUrl);
    if (!spreadsheetId) {
      NotificationService.error('Invalid Google Sheets URL. Please use a full URL containing a spreadsheet ID');
      return;
    }

    setIsSyncing(true);
    setSyncStatus('Starting sync...');
    setErrors([]);

    try {
      const token = await getToken();
      client.setConfig({
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const response = await triggerSheetsSyncApiV1SheetsSyncDataTypePost({
        body: {
          spreadsheet_id: spreadsheetId,
          range: range,
        },
        path: {
          data_type: syncType,
        },
      });

      if (response.data) {
        setSyncStatus('Sync started! Processing...');
        pollSyncStatus(response.data.upload_id);
      }

      if (response.error) {
        NotificationService.error('Sync failed: ' + response.error.detail, 10000);
        setSyncStatus('Sync failed');
      }
    } catch (error) {
      NotificationService.error('Sync failed');
      setSyncStatus('Sync failed');
    } finally {
      setIsSyncing(false);
    }
  };

  const pollSyncStatus = async (syncId: number) => {
    try {
      const response = await getSyncStatusApiV1SheetsSyncSyncIdGet({
        path: { sync_id: syncId },
      });

      if (response.data && response.data.status === 'completed') {
        setSyncStatus('Sync complete');
        if (response.data.error_count && response.data.error_count > 0) {
          setErrors([{ message: `Sync completed with ${response.data.error_count} errors` }]);
        } else {
          NotificationService.success(`Successfully synced ${response.data.success_count} rows`);
        }
      } else {
        setTimeout(() => pollSyncStatus(syncId), 5000);
      }
    } catch (error) {
      NotificationService.error('Error polling sync status: ' + error, 5000);
    }
  };

  return (
    <PageLayout pageId={pageId}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Google Sheets URL"
            value={sheetUrl}
            onChange={(e) => setSheetUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/SPREADSHEET_ID/edit"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Data Range"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            placeholder="Sheet1!A1:D100"
          />
        </Box>

        <Button
          variant="contained"
          onClick={handleSync}
          disabled={!sheetUrl || !range || isSyncing}
          startIcon={isSyncing ? <CircularProgress size={20} /> : <CloudSync />}
        >
          {isSyncing ? 'Syncing...' : 'Sync Data'}
        </Button>

        {syncStatus && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1">{syncStatus}</Typography>
          </Box>
        )}

        {errors.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sync Errors
            </Typography>
            {errors.map((error, index) => (
              <Alert key={index} severity="error" sx={{ mb: 1 }}>
                {JSON.stringify(error)}
              </Alert>
            ))}
          </Box>
        )}
      </Box>
    </PageLayout>
  );
};
